/**
 * Shared, feature-agnostic helpers for working out which manufacturer-specific
 * product a batch refers to.
 *
 * Background (issue #26027): a batch id looks like
 *   product:<genericProductId>:manufacturer:<value>:batchNo:<batchNo>
 * The `<value>` after `:manufacturer:` is NOT a product code — for
 * some batches it is a manufacturer *name* (e.g. `chemonics`). Inferring a
 * product from it (`product:chemonics`) is incorrect, so it is never used.
 * The real manufacturer-specific product is only discoverable from the batch
 * document's `alias` (e.g. `{ one: 'GHSC-AID0417H' }` -> `product:ghsc-aid0417h`).
 *
 * These functions are pure (no I/O) so every feature can share one
 * implementation. Callers fetch batch docs however their runtime allows and
 * pass them in.
 */

const get = require('lodash/get')

/**
 * Flattens an alias map into a plain list of alias code strings.
 * Accepts both `{ one: 'GHSC-AID0417H' }` and `{ one: ['A', 'B'] }`.
 * @param {Object|undefined} aliasMap
 * @returns {string[]}
 */
const aliasValuesOf = (aliasMap) => {
  return Object.values(aliasMap || {})
    .flatMap(entry => (Array.isArray(entry) ? entry : [entry]))
    .filter(value => typeof value === 'string' && value.length > 0)
}

const toProductId = (code) => `product:${String(code).toLowerCase()}`

/**
 * True when the product's declared generic parent matches the batch's generic
 * product. Used to reject alias values that resolve to a real but unrelated
 * product (dirty/ambiguous alias data). Products without a `genericParent` are
 * accepted as-is.
 */
const productMatchesGenericProduct = (product, genericProductId) => {
  if (!genericProductId) return true
  const genericParent = get(product, 'genericParent')
  if (!genericParent) return true
  return toProductId(genericParent) === String(genericProductId).toLowerCase()
}

/**
 * Manufacturer-specific product id derived from the batch document's `alias`.
 * @returns {string|null} `product:<code>` (lowercased) or null
 */
const getManufacturerProductIdFromAlias = (batch, { genericProductId, productsById } = {}) => {
  const codes = aliasValuesOf(batch && batch.alias)
  for (const code of codes) {
    const productId = toProductId(code)
    // Without product docs we cannot validate, so take the first alias code.
    if (!productsById) return productId
    const product = productsById[productId]
    if (product && (
      productId === String(genericProductId).toLowerCase() ||
      productMatchesGenericProduct(product, genericProductId)
    )) {
      return productId
    }
  }
  return null
}

/**
 * Single source of truth for "which manufacturer-specific product does this
 * batch point at?". The batch doc's alias is the sole source — the
 * `:manufacturer:` segment is never used to infer a product.
 *
 * Returns null when no alias resolves; the caller decides whether to fall back
 * to the generic product.
 *
 * @returns {string|null}
 */
const resolveManufacturerProductId = ({ batch, genericProductId, productsById } = {}) => {
  return getManufacturerProductIdFromAlias(batch, { genericProductId, productsById })
}

module.exports = {
  aliasValuesOf,
  getManufacturerProductIdFromAlias,
  productMatchesGenericProduct,
  resolveManufacturerProductId
}
